import type { ScopeFilter } from '@@/api/health';
import type { HealthSeverity } from '@@/api/health/health-rollup';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SearchInput } from '@/components/SearchInput';
import { useHealthRollup } from '@/state/useHealthRollup';
import { DisclosureButton, EmptyState, MetricGrid, MetricTile, Panel } from '../ConsolePanel';
import { ScopePills, SEARCH_FIELD_CLASS } from '../ConsolePanel';
import { ClientCard } from './ClientCard';

/**
 * What is wrong across every account the viewer can reach.
 *
 * Separate from the dashboard rather than a mode of it: the dashboard is scoped
 * to a single website by the header picker, and this question spans all of
 * them. Folding both into one screen would mean a page whose meaning changes
 * depending on a control somewhere else.
 *
 * Not agency-only. An agency sees its client roster, a direct customer sees
 * their own account and the same signals. Scope is resolved server-side from
 * the session, so there is no account id in the URL to gate or to tamper with.
 */
export const HealthScreen = () => {
  const navigate = useNavigate();
  const { rollup, isLoading, isError, refetch, scope, setScope, search, setSearch } = useHealthRollup();
  const [hideQuiet, setHideQuiet] = useState(false);

  /**
   * Which severity the rollup tiles have narrowed to, if any.
   *
   * A count is a question - "seven need a decision, which seven?" - and answering it
   * used to mean reading every account card looking for amber dots. Selecting a tile
   * filters the cards below to that severity and drops the accounts left with nothing.
   */
  const [severity, setSeverity] = useState<HealthSeverity | null>(null);
  const toggleSeverity = (next: HealthSeverity) => setSeverity((current) => (current === next ? null : next));

  if (isLoading) {
    return <Panel title="Health" description="Loading every account you can reach." />;
  }

  /*
    A failed rollup used to land on the loading panel above and stay there: the request
    is finished, so isLoading is false, and the data is undefined either way. Saying so
    is the difference between a page that is slow and a page that is broken.
  */
  if (isError || !rollup) {
    return (
      <Panel title="Health" description="Could not reach the health rollup.">
        <div className="flex flex-col items-start gap-2">
          <EmptyState>Nothing was loaded, so this page cannot tell you what is wrong.</EmptyState>
          <DisclosureButton onClick={() => refetch()}>Try again</DisclosureButton>
        </div>
      </Panel>
    );
  }

  const { totals, clients, isAgency } = rollup;
  const mineOnly = scope === 'mine';

  const hasSomething = (client: (typeof clients)[number]) =>
    client.totals.itemCount > 0 || client.totals.runningCount > 0;

  const quiet = clients.filter((client) => !hasSomething(client));
  // Filtering to one person is itself a request to see less, so the dormant
  // accounts collapse automatically there. Otherwise every account is listed:
  // an agency asked for its whole roster, and a page that silently omits most
  // of it cannot be trusted as one.
  const collapse = hideQuiet || mineOnly;
  const listed = collapse ? clients.filter(hasSomething) : clients;

  /**
   * With a severity selected the page answers one question, so everything that is not
   * part of the answer goes: other severities, websites left with no items, and
   * accounts left with no websites. Without one, nothing is touched.
   */
  const visible = !severity
    ? listed
    : clients
        .map((client) => ({
          ...client,
          websites: client.websites
            .map((website) => ({ ...website, items: website.items.filter((item) => item.severity === severity) }))
            .filter((website) => website.items.length > 0),
        }))
        .filter((client) => client.websites.length > 0);

  return (
    <div className="flex flex-col gap-3.5">
      <Panel
        title="Health"
        description={
          isAgency
            ? 'Running experiments across every client account, ordered by what needs you first.'
            : 'Running experiments in your account, ordered by what needs you first.'
        }
        actions={
          <>
            {/* Wrapped only to carry the tour anchor, at the width the pill strip takes. */}
            <div className="w-full md:w-auto" data-tour="health-scope">
              <ScopePills value={scope} onChange={(next) => setScope(next as ScopeFilter)} isAgency={isAgency} />
            </div>
            {/* `relative`, and full width on a phone: SearchInput collapses to a
                magnifier below `md` and drops its field in absolutely, so it needs a
                positioned parent of the width the field should take. */}
            <div className="relative w-full md:w-auto">
              <SearchInput
                className={SEARCH_FIELD_CLASS}
                handleSearch={setSearch}
                value={search}
                placeholder="Search accounts, websites, experiments..."
              />
            </div>
          </>
        }
      >
        {mineOnly && (
          <p className="supporting mb-2.5">
            Experiments you created or started. Accounts with none of yours are hidden.
          </p>
        )}

        <div data-tour="health-tiles">
          <MetricGrid>
            <MetricTile
              label={isAgency ? 'Accounts' : 'Account'}
              value={String(totals.clientCount)}
              description={isAgency ? 'Client accounts you can reach' : 'Your account'}
              subtitle={`${totals.websiteCount} website${totals.websiteCount === 1 ? '' : 's'}`}
            />
            {/* Running is a count, not a list: the rollup only carries the experiments
              that need something, so there is nothing here to filter down to. It goes
              to the library's running view instead, which is the list. */}
            <MetricTile
              label="Running"
              value={String(totals.runningCount)}
              valueClassName="text-ok"
              description="Experiments live now"
              subtitle="Open the running list"
              onClick={() => navigate('/experiments/library?view=Running')}
            />
            <MetricTile
              label="Needs a decision"
              value={String(totals.decideCount)}
              valueClassName="text-warn"
              description="Concluded, waiting on a call"
              subtitle={severity === 'decide' ? 'Showing only these' : 'Winners, losers and overdue tests'}
              isActive={severity === 'decide'}
              onClick={() => toggleSeverity('decide')}
            />
            <MetricTile
              label="Broken"
              value={String(totals.brokenCount)}
              valueClassName="text-danger"
              description="Do not act on these results"
              subtitle={severity === 'broken' ? 'Showing only these' : 'Split mismatch or no traffic'}
              isActive={severity === 'broken'}
              onClick={() => toggleSeverity('broken')}
            />
          </MetricGrid>
        </div>

        {!!severity && (
          <div className="border-line mt-2.5 flex flex-wrap items-center gap-2 border-t pt-2.5">
            <span className="supporting">
              Showing only {severity === 'broken' ? 'broken experiments' : 'experiments waiting on a decision'}.
            </span>
            <DisclosureButton onClick={() => setSeverity(null)}>Show everything</DisclosureButton>
          </div>
        )}
      </Panel>

      {visible.length === 0 ? (
        <Panel title="Accounts" description={search ? 'No matches.' : 'Nothing needs you.'}>
          <EmptyState>
            {/* The severity case comes first, or a filtered-to-empty page would claim
                nothing is wrong anywhere while a filter is quietly hiding the rest. */}
            {severity
              ? severity === 'broken'
                ? 'Nothing is broken right now.'
                : 'Nothing is waiting on a decision.'
              : search
                ? `Nothing matches "${search}".`
                : mineOnly
                  ? 'No running experiments were created or started by you.'
                  : 'Nothing is running and nothing is wrong.'}
          </EmptyState>
        </Panel>
      ) : (
        visible.map((client, index) => (
          <ClientCard key={client.companyId} client={client} tourAnchor={index === 0 ? 'health-accounts' : undefined} />
        ))
      )}

      {/* The quiet-account toggle is about the unfiltered roster, so it has no meaning
          while a severity is narrowing the page. */}
      {!severity && !mineOnly && !search && quiet.length > 0 && (
        <DisclosureButton onClick={() => setHideQuiet((prev) => !prev)}>
          {hideQuiet
            ? `Show ${quiet.length} quiet account${quiet.length === 1 ? '' : 's'}`
            : `Hide ${quiet.length} quiet account${quiet.length === 1 ? '' : 's'} (nothing running, nothing wrong)`}
        </DisclosureButton>
      )}
    </div>
  );
};
